import { AvatarDropzone, Form, Grid, Select, Text, useExtensions } from '@approvalmax/ui/src/components';
import { memo, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import { maxDocumentSize } from './PersonIdDocument.constants';
import { useDocumentTypes } from './PersonIdDocument.hooks';
import { messages } from './PersonIdDocument.messages';
import { schema } from './PersonIdDocument.schema';

export const PersonIdDocument = memo(() => {
    const { documentTypes, isDocumentTypesLoading } = useDocumentTypes();

    const { watch, setValue } = useFormContext<z.infer<typeof schema>>();
    const documentType = watch('person.personDocuments.documentType');
    const requireBack = useMemo(() => {
        const typeConfig = documentTypes.find((typeConfig) => typeConfig.id === documentType);

        return typeConfig?.requiredBack || false;
    }, [documentType, documentTypes]);
    const extensions = useExtensions(['png', 'jpg', 'pdf']);

    useEffect(() => {
        setValue('person.personDocuments.requireBack', requireBack);
    }, [requireBack, setValue]);

    return (
        <Form.Part>
            <Grid gap={16}>
                <Text font='headline' fontSize='xxsmall' fontWeight='medium'>
                    {messages.idDocument}
                </Text>

                <Text font='body' fontSize='medium' fontWeight='regular'>
                    {messages.pleaseProvideDocs}
                </Text>

                <Select
                    name='person.personDocuments.documentType'
                    label={messages.docType}
                    placeholder={messages.docTypePlaceholder}
                    items={documentTypes}
                    progress={isDocumentTypesLoading}
                />

                <Grid gridAutoFlow='column' gridAutoColumns='50%'>
                    <AvatarDropzone
                        required
                        rounded={false}
                        name='person.personDocuments.front'
                        label={messages.front}
                        hint={messages.requirements}
                        accept={extensions}
                        maxSize={maxDocumentSize}
                    />

                    {requireBack && (
                        <AvatarDropzone
                            required
                            rounded={false}
                            name='person.personDocuments.back'
                            label={messages.back}
                            hint={messages.requirements}
                            accept={extensions}
                            maxSize={maxDocumentSize}
                        />
                    )}
                </Grid>
            </Grid>
        </Form.Part>
    );
});

PersonIdDocument.displayName = 'PersonIdDocument';
